import { createSlice } from '@reduxjs/toolkit';
import {
  requestShiftSwapThunk,
  acceptShiftSwapThunk,
  declineShiftSwapThunk,
  cancelShiftSwapThunk,
  fetchShiftSwapRequestsThunk,
  fetchShiftSwapRequestThunk,
} from './shiftSwapThunks';

const initialState = {
  requests: [],
  currentRequest: null,
  isLoading: false,
  error: null,
  requestStatus: 'idle', // 'idle' | 'pending' | 'succeeded' | 'failed'
  // Add optimistic updates for shifts
  optimisticShiftUpdates: {}, // Keyed by shiftId, contains pendingSwap ID
};

const shiftSwapSlice = createSlice({
  name: 'shiftSwap',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null;
    },
    setRequests: (state, action) => {
      state.requests = action.payload;
    },
    // Add optimistic update for shift swap request
    addOptimisticShiftUpdate: (state, action) => {
      const { shiftId, swapRequestId } = action.payload;
      state.optimisticShiftUpdates[shiftId] = swapRequestId;
    },
    // Clear optimistic update when real data arrives
    clearOptimisticShiftUpdate: (state, action) => {
      const { shiftId } = action.payload;
      delete state.optimisticShiftUpdates[shiftId];
    },
    // Reset request status to idle
    resetRequestStatus: (state) => {
      state.requestStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Request shift swap
      .addCase(requestShiftSwapThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.requestStatus = 'pending';
        // Add optimistic update immediately
        const { shiftId } = action.meta.arg;
        state.optimisticShiftUpdates[shiftId] = 'pending'; // Temporary ID
      })
      .addCase(requestShiftSwapThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.requestStatus = 'succeeded';
        // optimistic update until real data comes through real-time listener, The shift slice will maintain the pending status until updated by real-time data
      })
      .addCase(requestShiftSwapThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.requestStatus = 'failed';
        // Clear optimistic update on error
        const { shiftId } = action.meta.arg;
        delete state.optimisticShiftUpdates[shiftId];
      })

      // Accept shift swap
      .addCase(acceptShiftSwapThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptShiftSwapThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove the accepted request from the list
        state.requests = state.requests.filter((request) => request.id !== action.payload.shiftSwapRequestId);
      })
      .addCase(acceptShiftSwapThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Decline shift swap
      .addCase(declineShiftSwapThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(declineShiftSwapThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove the declined request from the list
        state.requests = state.requests.filter((request) => request.id !== action.payload.shiftSwapRequestId);
      })
      .addCase(declineShiftSwapThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Cancel shift swap (same as decline but for source user)
      .addCase(cancelShiftSwapThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelShiftSwapThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requestStatus = 'idle';
        // Remove the canceled request from the list
        state.requests = state.requests.filter((request) => request.id !== action.payload.shiftSwapRequestId);
      })
      .addCase(cancelShiftSwapThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch shift swap requests
      .addCase(fetchShiftSwapRequestsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShiftSwapRequestsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requests = action.payload;
      })
      .addCase(fetchShiftSwapRequestsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch single shift swap request
      .addCase(fetchShiftSwapRequestThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShiftSwapRequestThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentRequest = action.payload;
      })
      .addCase(fetchShiftSwapRequestThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Selectors
export const selectShiftSwapRequests = (state) => state.shiftSwap.requests;
export const selectCurrentShiftSwapRequest = (state) => state.shiftSwap.currentRequest;
export const selectShiftSwapLoading = (state) => state.shiftSwap.isLoading;
export const selectShiftSwapError = (state) => state.shiftSwap.error;
export const selectShiftSwapRequestStatus = (state) => state.shiftSwap.requestStatus;

export const { clearError, clearCurrentRequest, setRequests, addOptimisticShiftUpdate, clearOptimisticShiftUpdate, resetRequestStatus } =
  shiftSwapSlice.actions;

export default shiftSwapSlice.reducer;
